import { useState, useRef, useEffect, useId } from "react";
import { useTranslation } from "react-i18next";
import styles from "./SearchableSelect.module.css";

export interface SearchableSelectOption {
	value: string;
	label: string;
	meta?: Record<string, string>;
}

interface SearchableSelectProps {
	value?: string;
	onChange: (value: string, option?: SearchableSelectOption) => void;
	options?: SearchableSelectOption[];
	loadOptions?: (query: string) => Promise<SearchableSelectOption[]>;
	initialOption?: SearchableSelectOption | undefined;
	placeholder?: string;
	loading?: boolean;
	disabled?: boolean;
	clearable?: boolean;
	label?: string;
}

export function SearchableSelect({
	value,
	onChange,
	options: staticOptions,
	loadOptions,
	initialOption,
	placeholder,
	loading: externalLoading,
	disabled,
	clearable,
	label,
}: SearchableSelectProps) {
	const { t } = useTranslation();
	const id = useId();
	const [open, setOpen] = useState(false);
	const [query, setQuery] = useState("");
	const [items, setItems] = useState<SearchableSelectOption[]>(staticOptions ?? []);
	const [loading, setLoading] = useState(false);
	const [highlightedIndex, setHighlightedIndex] = useState(0);
	const containerRef = useRef<HTMLDivElement>(null);
	const inputRef = useRef<HTMLInputElement>(null);
	const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const loadOptionsRef = useRef(loadOptions);
	const staticOptionsRef = useRef(staticOptions);

	useEffect(() => {
		loadOptionsRef.current = loadOptions;
		staticOptionsRef.current = staticOptions;
	}, [loadOptions, staticOptions]);

	const [selectedOption, setSelectedOption] = useState<SearchableSelectOption | undefined>(
		initialOption,
	);

	const selectedLabel =
		items.find((o) => o.value === value)?.label ??
		(value && selectedOption?.value === value ? selectedOption.label : undefined) ??
		(value ? initialOption?.label : undefined) ??
		"";

	useEffect(() => {
		if (debounceRef.current) clearTimeout(debounceRef.current);
		debounceRef.current = setTimeout(() => {
			const lo = loadOptionsRef.current;
			const so = staticOptionsRef.current;
			const q = query;
			if (!lo) {
				if (so) {
					const filtered = so.filter((o) => o.label.toLowerCase().includes(q.toLowerCase()));
					setItems(filtered);
				}
				return;
			}
			setLoading(true);
			lo(q)
				.then((result) => setItems(result))
				.catch(() => setItems([]))
				.finally(() => setLoading(false));
			setHighlightedIndex(0);
		}, 300);
		return () => {
			if (debounceRef.current) clearTimeout(debounceRef.current);
		};
	}, [query]);

	useEffect(() => {
		function onClickOutside(e: MouseEvent) {
			if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
				setOpen(false);
			}
		}
		if (open) {
			document.addEventListener("mousedown", onClickOutside);
		}
		return () => document.removeEventListener("mousedown", onClickOutside);
	}, [open]);

	const handleSelect = (option: SearchableSelectOption) => {
		setSelectedOption(option);
		onChange(option.value, option);
		setQuery("");
		setOpen(false);
	};

	const handleClear = (e: React.MouseEvent) => {
		e.stopPropagation();
		setSelectedOption(undefined);
		onChange("");
		setQuery("");
		inputRef.current?.focus();
	};

	const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (!open) {
			if (e.key === "ArrowDown" || e.key === "Enter") {
				setOpen(true);
			}
			return;
		}
		if (e.key === "ArrowDown") {
			e.preventDefault();
			setHighlightedIndex((i) => Math.min(i + 1, items.length - 1));
		} else if (e.key === "ArrowUp") {
			e.preventDefault();
			setHighlightedIndex((i) => Math.max(i - 1, 0));
		} else if (e.key === "Enter") {
			e.preventDefault();
			const opt = items[highlightedIndex];
			if (opt) handleSelect(opt);
		} else if (e.key === "Escape") {
			setOpen(false);
		}
	};

	const displayValue = open ? query : selectedLabel;

	return (
		<div className={styles.wrapper} ref={containerRef}>
			{label && (
				<label htmlFor={id} className={styles.label}>
					{label}
				</label>
			)}
			<div
				className={[styles.inputWrap, open && styles.open, disabled && styles.disabled]
					.filter(Boolean)
					.join(" ")}
			>
				<input
					id={id}
					ref={inputRef}
					type="text"
					className={styles.input}
					value={displayValue}
					placeholder={placeholder}
					disabled={disabled}
					onChange={(e) => {
						setQuery(e.target.value);
						if (!open) setOpen(true);
					}}
					onFocus={() => {
						setOpen(true);
						if (loadOptions && items.length === 0) {
							setQuery("");
						}
					}}
					onKeyDown={handleKeyDown}
					autoComplete="off"
				/>
				{clearable && value && (
					<button type="button" className={styles.clear} onClick={handleClear} tabIndex={-1}>
						<svg
							width="14"
							height="14"
							viewBox="0 0 24 24"
							fill="none"
							stroke="currentColor"
							strokeWidth="2"
							strokeLinecap="round"
							strokeLinejoin="round"
						>
							<path d="M18 6 6 18" />
							<path d="m6 6 12 12" />
						</svg>
					</button>
				)}
				<span className={styles.chevron}>
					<svg
						width="14"
						height="14"
						viewBox="0 0 24 24"
						fill="none"
						stroke="currentColor"
						strokeWidth="2"
						strokeLinecap="round"
						strokeLinejoin="round"
					>
						<path d="m6 9 6 6 6-6" />
					</svg>
				</span>
			</div>
			{open && (
				<div className={styles.dropdown}>
					{(loading || externalLoading) && (
						<div className={styles.empty}>{t("loadingOptions")}</div>
					)}
					{!loading && !externalLoading && items.length === 0 && (
						<div className={styles.empty}>{t("noResults")}</div>
					)}
					{!loading &&
						!externalLoading &&
						items.map((item, idx) => (
							<div
								key={item.value}
								className={[
									styles.item,
									idx === highlightedIndex && styles.highlighted,
									item.value === value && styles.selected,
								]
									.filter(Boolean)
									.join(" ")}
								onMouseEnter={() => setHighlightedIndex(idx)}
								onClick={() => handleSelect(item)}
							>
								{item.label}
							</div>
						))}
				</div>
			)}
		</div>
	);
}
